.root {
    /* ldGray-7 resolves per scheme: #495057 light (7.5:1 on white), #9e9e9e dark */
    --toast-text: var(--mantine-color-ldGray-7);

    padding: 14px 14px 14px 16px;
    background: linear-gradient(
        180deg,
        var(--mantine-color-white) 0%,
        var(--mantine-color-ldGray-0) 100%
    );
    border: 1px solid var(--mantine-color-ldGray-3);
    border-radius: 10px;
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.9),
        var(--mantine-shadow-heavy);

    @mixin dark {
        background: linear-gradient(
            180deg,
            var(--mantine-color-ldGray-3) 0%,
            var(--mantine-color-ldGray-2) 100%
        );
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            0px 12px 16px -4px rgba(0, 0, 0, 0.5),
            0px 2px 2px -1px rgba(0, 0, 0, 0.4);
    }
}

.root:has(.description:not(:empty)) {
    align-items: flex-start;
}

.root :global(.mantine-Notification-body) {
    margin-right: 12px;
}

.title {
    color: light-dark(
        var(--mantine-color-ldGray-9),
        var(--mantine-color-foreground-0)
    );
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.15px;
    margin-bottom: 2px;
}

.root:has(.description:empty) .title {
    margin-bottom: 0;
}

.description {
    color: var(--toast-text);
    font-size: 13px;
    line-height: 18px;
}

.closeButton {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    border-radius: 6px;
    color: var(--mantine-color-ldGray-6);

    &:hover {
        background: light-dark(
            var(--mantine-color-ldGray-1),
            rgba(255, 255, 255, 0.08)
        );
        color: light-dark(
            var(--mantine-color-ldGray-7),
            var(--mantine-color-foreground-0)
        );
    }
}

.icon {
    width: 18px;
    height: 18px;
    margin-top: 1px;
    margin-right: 12px;
    background-color: transparent;
}

/* 12px loader centered on the 20px title line, matching the 18px icon */
.loader {
    margin-top: 4px;
    margin-right: 12px;
}

.subtitle {
    color: var(--toast-text);
    font-size: 13px;
    line-height: 18px;
    width: 100%;
}

.markdown {
    background-color: transparent;
    color: var(--toast-text);
    font-size: 13px;
    line-height: 18px;
}

.actionButton {
    margin-top: 8px;
    height: 28px;
    padding: 0 10px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.15px;
    border-radius: 6px;
}

.root[data-variant='success'] .icon {
    color: light-dark(
        var(--mantine-color-green-6),
        var(--mantine-color-green-5)
    );
}

.root[data-variant='info'] .icon {
    color: var(--toast-text);
}

/* orange-7 is the lightest step that keeps >=3:1 non-text contrast on white */
.root[data-variant='warning'] .icon {
    color: light-dark(
        var(--mantine-color-orange-7),
        var(--mantine-color-orange-4)
    );
}

.root[data-variant='error'] {
    background: color-mix(
        in srgb,
        var(--mantine-color-red-0) 62%,
        var(--mantine-color-white)
    );
    border-color: color-mix(
        in srgb,
        var(--mantine-color-red-8) 20%,
        transparent
    );
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.7),
        var(--mantine-shadow-heavy);

    @mixin dark {
        background: color-mix(
            in srgb,
            var(--mantine-color-red-5) 10%,
            var(--mantine-color-ldGray-2)
        );
        border-color: color-mix(
            in srgb,
            var(--mantine-color-red-5) 28%,
            transparent
        );
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            0px 12px 16px -4px rgba(0, 0, 0, 0.5),
            0px 2px 2px -1px rgba(0, 0, 0, 0.4);
    }
}

.root[data-variant='error'] .icon {
    color: light-dark(var(--mantine-color-red-8), var(--mantine-color-red-5));
}

.root[data-variant='error'] .title {
    color: light-dark(var(--mantine-color-red-9), var(--mantine-color-red-3));
}

/* Expanded error details: outgrow the inline max-height Mantine sets for its
   enter/exit animation (inline style, hence !important) and widen leftwards
   via negative margin so the right edge stays anchored to the stack. */
.root:has([data-toast-expanded]) {
    --toast-collapsed-width: min(
        var(--notifications-container-width, 440px),
        calc(100vw - 32px)
    );
    --toast-expanded-width: min(680px, calc(100vw - 32px));
    width: var(--toast-expanded-width);
    margin-left: calc(
        var(--toast-collapsed-width) - var(--toast-expanded-width)
    );
    max-height: calc(100vh - 96px) !important;
}

.root[data-variant='error'] .closeButton {
    color: light-dark(var(--mantine-color-red-8), var(--mantine-color-red-5));

    &:hover {
        background: light-dark(
            color-mix(in srgb, var(--mantine-color-red-8) 10%, transparent),
            color-mix(in srgb, var(--mantine-color-red-5) 15%, transparent)
        );
        color: light-dark(
            var(--mantine-color-red-9),
            var(--mantine-color-red-3)
        );
    }
}
